import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DataService } from 'src/app/core/services/data/data.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import {
  ILotRateList,
  IParkerMassUpdate,
  IParkerMassUpdateCompany,
  IParkerMassUpdateInvoiceDeliveryMethod,
  IParkerMassUpdateParkerPayType,
  IParkerMassUpdateRate,
  IParkerMassUpdateTermDate,
  IStallTypes,
} from 'src/app/shared/interfaces';
import { DataBroadcastService } from 'src/app/core/services/broadcast/data-broadcast.service';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AppGeneralStateService } from 'src/app/app-general-state.service';

@Component({
  selector: 'app-parkers-v2-massupdate',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    ReactiveFormsModule,
  ],
  templateUrl: './parkers-v2-massupdate.component.html',
  styleUrl: './parkers-v2-massupdate.component.scss',
})
export class ParkersV2MassupdateComponent implements OnInit {
  massUpdateForm: FormGroup;
  updateButtonDisabled: boolean = true;
  marketRateList: ILotRateList[] = [];
  massUpdateResult: IParkerMassUpdate;
  stallDescriptions: IStallTypes[] = [];
  currentLotNumber: string;

  //Services
  #_appGeneralState = inject(AppGeneralStateService);

  constructor(
    public parkerStateService: ParkerStateService,
    public dataService: DataService,
    private _snackBar: MatSnackBar,
    private dataBroadcastService: DataBroadcastService,
  ) {
    this.dataBroadcastService.validateMassUpdateEnabled.subscribe((res: any) => {
      this.evalMassUpdateReadyForSubmit();
    });
  }

  ngOnInit(): void {
    this.currentLotNumber = this.parkerStateService.filteredParkers[0].lotNumber;
    this.initMassUpdateForm();
    this.parkerStateService.GetLotRateUsageMetrics(this.currentLotNumber);
    this.getStallDescriptionListForLotNumber(this.parkerStateService.filteredParkers[0].lotNumber);
    this.getMarketRateListByLotNumber(this.parkerStateService.filteredParkers[0].lotNumber);
  }

  initMassUpdateForm(): void {
    this.massUpdateForm = new FormGroup({
      updateCompany: new FormControl(false, []),
      companyId: new FormControl('NONE', []),
      updateRate: new FormControl(false, []),
      rateNumber: new FormControl('NONE', []),
      updateParkerPayType: new FormControl(false, []),
      parkerPayType: new FormControl('ALL', []),
      updateInvoiceDeliveryDate: new FormControl(false, []),
      invoiceDeliveryMethod: new FormControl('ALL', []),
      termDate: new FormControl('', []),
      updateTermDate: new FormControl(false, []),
    });

    this.massUpdateForm.get('companyId')!.disable();
    this.massUpdateForm.get('rateNumber')!.disable();
    this.massUpdateForm.get('parkerPayType')!.disable();
    this.massUpdateForm.get('invoiceDeliveryMethod')!.disable();
    this.massUpdateForm.get('termDate')!.disable();
  }

  GetTextForLotRateUsageMetrics(rateNumber: number, rateType: string): string {
    let metricText: string = 'N/A';
    let ii: number = 0;
    let quantityUsed: number = 0;
    let limit: number = 0;
    let companyId: string = '';

    try {
      companyId = this.massUpdateForm.get('companyId').value;

      if (companyId == '' || companyId == 'NONE') {
        metricText = '0/0';
      } else {
        for (ii = 0; ii < this.parkerStateService.lotRateUsageMetrics.length; ii++) {
          if (
            this.parkerStateService.lotRateUsageMetrics[ii].rateNumber == rateNumber &&
            this.parkerStateService.lotRateUsageMetrics[ii].companyId == companyId
          ) {
            quantityUsed = this.parkerStateService.lotRateUsageMetrics[ii].used;
            limit = this.parkerStateService.lotRateUsageMetrics[ii].limit;
          }
        }
        metricText = quantityUsed.toLocaleString() + '/' + limit.toLocaleString();
      }
    } catch (err) {
      console.error('Error: ' + JSON.stringify(err));
      metricText = 'N/A';
    }

    return metricText;
  }

  onSubmitMassUpdate(): void {
    let parkerMassUpdate: IParkerMassUpdate = {} as IParkerMassUpdate;

    parkerMassUpdate.company = this.setMassUpdateCompany();
    parkerMassUpdate.rate = this.setMassUpdateRate();
    parkerMassUpdate.parkerPayType = this.setMassUpdateParkerPay();
    parkerMassUpdate.invoiceDeliveryType = this.setMassUpdateInvoiceDelivery();
    parkerMassUpdate.termDate = this.setMassUpdateTermDate();

    if (this.massUpdatesPreparedForSubmission(parkerMassUpdate)) {
      this.postMassUpdate(parkerMassUpdate);
    }
  }

  postMassUpdate(massUpdate: IParkerMassUpdate): void {
    let postStatus: boolean;
    this.dataService.postParkerMassUpdate(massUpdate, this.currentLotNumber).subscribe(
      (res) => {
        this.massUpdateResult = res;
      },
      (err: any) => {},
      () => {
        this.processMassUpdatePostResults();
        this.resetUpdateCriteria();
        this.parkerStateService.GetLotRateUsageMetrics(this.currentLotNumber);
      },
    );
  }

  resetUpdateCriteria(): void {
    this.massUpdateForm.patchValue({
      updatteCompany: '',
      updateRate: '',
      setMassUpdateParkerPay: '',
      updateInvoiceDeliveryDate: '',
      parkerPayType: 'ALL',
      rateNumber: 'NONE',
      invoiceDeliveryMethod: 'ALL',
      companyId: 'NONE',
      termDate: '',
      updateTermDate: '',
    });

    this.massUpdateForm.get('companyId')!.disable();
    this.massUpdateForm.get('rateNumber')!.disable();
    this.massUpdateForm.get('invoiceDeliveryMethod')!.disable();
    this.massUpdateForm.get('parkerPayType')!.disable();
    this.massUpdateForm.get('termDate')!.disable();

    this.updateButtonDisabled = true; //controls disable/enable on submit button to execute mass update
  }

  processMassUpdatePostResults(): void {
    let totalUpdates: number = 0;
    let totalErrors: number = 0;
    let ii: number;

    try {
      if (this.massUpdateResult.termDate.length > 0)
        this.parkerStateService.updateParkerTermDateDataAfterMassUpdate(this.massUpdateResult.termDate);

      // for (ii = 0; ii < this.massUpdateResult.termDate.length; ii++) {
      //   totalUpdates++;
      //   if (this.massUpdateResult.termDate[ii].status == false) {
      //     totalErrors++;
      //     this.setParkerUpdateToError(this.massUpdateResult.termDate[ii].parkerId);
      //   }
      // }
    } catch (err) {}

    try {
      if (this.massUpdateResult.company.length > 0)
        this.parkerStateService.updateParkerCompanyDataAfterMassUpdate(this.massUpdateResult.company);

      // for (ii = 0; ii < this.massUpdateResult.company.length; ii++) {
      //   totalUpdates++;
      //   if (this.massUpdateResult.company[ii].status == false) {
      //     totalErrors++;
      //     this.setParkerUpdateToError(this.massUpdateResult.company[ii].parkerId);
      //   }
      // }
    } catch (err) {}

    try {
      if (this.massUpdateResult.rate.length > 0)
        this.parkerStateService.updateParkerRateDataAfterMassUpdate(this.massUpdateResult.rate);

      // for (ii = 0; ii < this.massUpdateResult.rate.length; ii++) {
      //   totalUpdates++;
      //   if (this.massUpdateResult.rate[ii].status == false) {
      //     totalErrors++;
      //     this.setParkerUpdateToError(this.massUpdateResult.rate[ii].parkerId);
      //   }
      // }
    } catch (err) {}

    try {
      if (this.massUpdateResult.parkerPayType.length > 0)
        this.parkerStateService.updateParkerPayTypeDataAfterMassUpdate(this.massUpdateResult.parkerPayType);

      // for (ii = 0; ii < this.massUpdateResult.parkerPayType.length; ii++) {
      //   totalUpdates++;
      //   if (this.massUpdateResult.parkerPayType[ii].status == false) {
      //     totalErrors++;
      //     this.setParkerUpdateToError(this.massUpdateResult.parkerPayType[ii].parkerId);
      //   }
      // }
    } catch (err) {}

    try {
      if (this.massUpdateResult.invoiceDeliveryType.length > 0)
        this.parkerStateService.updateParkerInvoiceDeliveryMethodDataAfterMassUpdate(
          this.massUpdateResult.invoiceDeliveryType,
        );

      // for (ii = 0; ii < this.massUpdateResult.invoiceDeliveryType.length; ii++) {
      //   totalUpdates++;
      //   if (this.massUpdateResult.invoiceDeliveryType[ii].status == false) {
      //     totalErrors++;
      //     this.setParkerUpdateToError(this.massUpdateResult.invoiceDeliveryType[ii].parkerId);
      //   }
      // }
    } catch (err) {}
    this.updateMassUpdateStatusResultStatus();
    this.parkerStateService.lastMassUpdateDateTime = new Date();
    this.setUpdateProcessToCompleted();
    this.#_appGeneralState.updateFinishedBroadcast.next(true);
    this.setPostProcessUserStatusDialog(totalUpdates, totalErrors);
  }

  updateMassUpdateStatusResultStatus(): void {
    let ii: number = 0;

    for (ii = 0; ii < this.parkerStateService.parkerUpdateList.length; ii++) {
      this.parkerStateService.parkerUpdateList[ii].status = this.massUpdateResult.results[ii].status;

      if (this.massUpdateResult.results[ii].parcsError == true) {
        this.parkerStateService.parkerUpdateList[ii].errorSource = 'PARCS Update';
      }
      if (this.massUpdateResult.results[ii].spaceArError == true) {
        this.parkerStateService.parkerUpdateList[ii].errorSource = 'spACE AR';
      }

      this.parkerStateService.parkerUpdateList[ii].description = ii.toString();
    }
  }

  setUpdateProcessToCompleted(): void {
    let ii: number;

    for (ii = 0; ii < this.parkerStateService.parkerUpdateList.length; ii++) {
      this.parkerStateService.parkerUpdateList[ii].processed = true;
    }

    //clear flag on filtered parkers
    for (ii = 0; ii < this.parkerStateService.filteredParkers.length; ii++) {
      this.parkerStateService.filteredParkers[ii].massUpdateTargetFlag = false;
    }

    this.evalMassUpdateReadyForSubmit();
    this.parkerStateService.massUpdateResultsExist = true;
  }

  setParkerUpdateToError(parkerId: string): void {
    let ii: number;

    for (ii = 0; ii < this.parkerStateService.parkerUpdateList.length; ii++) {
      if (parkerId == this.parkerStateService.parkerUpdateList[ii].parkerId) {
        this.parkerStateService.parkerUpdateList[ii].isError = true;
      }
    }
  }

  setPostProcessUserStatusDialog(totalUpdates: number, totalErrors: number): void {
    let percentSuccess: number;
    let statusVerbiage: string;

    if (totalErrors === 0) {
      percentSuccess = 100;
      statusVerbiage = `The mass update completed with no errors.`;
    } else if (totalErrors === totalUpdates) {
      percentSuccess = 0;
      statusVerbiage = `The mass update failed.  All updates failed.`;
    } else {
      percentSuccess = totalUpdates / totalErrors;
      statusVerbiage = `The mass update completed but ${totalErrors} errors occurred.`;
    }

    let action: any;
    this._snackBar.open(statusVerbiage, action, {
      duration: 7000,
    });
  }

  massUpdatesPreparedForSubmission(massUpdate: IParkerMassUpdate): boolean {
    if (massUpdate.company.length > 0 && this.parkerStateService.parkerUpdateList.length > 0) return true;

    if (massUpdate.rate.length > 0 && this.parkerStateService.parkerUpdateList.length > 0) return true;

    if (massUpdate.parkerPayType.length > 0 && this.parkerStateService.parkerUpdateList.length > 0) return true;

    if (massUpdate.invoiceDeliveryType.length > 0 && this.parkerStateService.parkerUpdateList.length > 0) return true;

    if (massUpdate.termDate.length > 0 && this.parkerStateService.parkerUpdateList.length > 0) return true;

    return false;
  }

  setMassUpdateTermDate(): IParkerMassUpdateTermDate[] {
    let invoiceTermDateList: IParkerMassUpdateTermDate[] = [];
    let ii: number;
    const updateTermDate: boolean = this.massUpdateForm.get('updateTermDate')!.value;
    const termDate: string = this.massUpdateForm.get('termDate')!.value;

    if (updateTermDate === true && termDate != '') {
      for (ii = 0; ii < this.parkerStateService.parkerUpdateList.length; ii++) {
        let anUpdate: IParkerMassUpdateTermDate = {} as IParkerMassUpdateTermDate;

        anUpdate.parkerId = this.parkerStateService.parkerUpdateList[ii].parkerId;
        anUpdate.status = false;
        anUpdate.termDate = this.massUpdateForm.get('termDate')!.value;

        invoiceTermDateList.push(anUpdate);
      }
    }

    return invoiceTermDateList;
  }

  setMassUpdateInvoiceDelivery(): IParkerMassUpdateInvoiceDeliveryMethod[] {
    let invoiceDeliverTypeUpdateList: IParkerMassUpdateInvoiceDeliveryMethod[] = [];
    let ii: number;
    const updateInvoiceDeliveryDate: boolean = this.massUpdateForm.get('updateInvoiceDeliveryDate')!.value;
    const invoiceDeliveryMethod: string = this.massUpdateForm.get('invoiceDeliveryMethod')!.value;

    if (updateInvoiceDeliveryDate === true && invoiceDeliveryMethod != 'ALL') {
      for (ii = 0; ii < this.parkerStateService.parkerUpdateList.length; ii++) {
        let anUpdate: IParkerMassUpdateInvoiceDeliveryMethod = {} as IParkerMassUpdateInvoiceDeliveryMethod;

        anUpdate.parkerId = this.parkerStateService.parkerUpdateList[ii].parkerId;
        anUpdate.status = false;
        anUpdate.invoiceDeliveryMethod = this.massUpdateForm.get('invoiceDeliveryMethod')!.value;

        invoiceDeliverTypeUpdateList.push(anUpdate);
      }
    }

    return invoiceDeliverTypeUpdateList;
  }

  setMassUpdateParkerPay(): IParkerMassUpdateParkerPayType[] {
    let parkerPayTypeUpdateList: IParkerMassUpdateParkerPayType[] = [];
    let ii: number;
    const parkerPayTypeSelected: boolean = this.massUpdateForm.get('updateParkerPayType')!.value;
    const parkerPayType: string = this.massUpdateForm.get('parkerPayType')!.value;

    if (parkerPayTypeSelected === true && parkerPayType != 'ALL') {
      for (ii = 0; ii < this.parkerStateService.parkerUpdateList.length; ii++) {
        let anUpdate: IParkerMassUpdateParkerPayType = {} as IParkerMassUpdateParkerPayType;

        anUpdate.parkerId = this.parkerStateService.parkerUpdateList[ii].parkerId;
        anUpdate.status = false;
        anUpdate.parkerPayType = this.massUpdateForm.get('parkerPayType')!.value;

        parkerPayTypeUpdateList.push(anUpdate);
      }
    }

    return parkerPayTypeUpdateList;
  }

  setMassUpdateRate(): IParkerMassUpdateRate[] {
    let parkerRateUpdateList: IParkerMassUpdateRate[] = [];
    let ii: number;
    const rateSelected: boolean = this.massUpdateForm.get('updateRate')!.value;
    const rateNumber: string = this.massUpdateForm.get('rateNumber')!.value;

    if (rateSelected === true && rateNumber != 'NONE') {
      for (ii = 0; ii < this.parkerStateService.parkerUpdateList.length; ii++) {
        let anUpdate: IParkerMassUpdateRate = {} as IParkerMassUpdateRate;

        anUpdate.parkerId = this.parkerStateService.parkerUpdateList[ii].parkerId;
        anUpdate.status = false;
        anUpdate.newRateNumber = +this.massUpdateForm.get('rateNumber')!.value;
        anUpdate.lotNumber = this.currentLotNumber;

        parkerRateUpdateList.push(anUpdate);
      }
    }

    return parkerRateUpdateList;
  }

  setMassUpdateCompany(): IParkerMassUpdateCompany[] {
    let parkerUpdateCompanyList: IParkerMassUpdateCompany[] = [];
    let ii: number;

    const companySelected: boolean = this.massUpdateForm.get('updateCompany')!.value;
    const companyId: string = this.massUpdateForm.get('companyId')!.value;

    if (companySelected === true && companyId != 'NONE') {
      for (ii = 0; ii < this.parkerStateService.parkerUpdateList.length; ii++) {
        let anUpdate: IParkerMassUpdateCompany = {} as IParkerMassUpdateCompany;

        anUpdate.parkerId = this.parkerStateService.parkerUpdateList[ii].parkerId;
        anUpdate.status = false;
        anUpdate.newCompanyId = this.massUpdateForm.get('companyId')!.value;

        parkerUpdateCompanyList.push(anUpdate);
      }
    }

    return parkerUpdateCompanyList;
  }

  evalMassUpdateReadyForSubmit(): void {
    let companyReadyUpdate: boolean = false;
    let rateReadyUpdate: boolean = false;
    let parkerPayTypeReadyUpdate: boolean = false;
    let invoiceDeliveryMethodReadyUpdate: boolean = false;
    let termDateReadyUpdate: boolean = false;

    const updateCompanyFlag: boolean = Boolean(this.massUpdateForm.get('updateCompany')!.value);
    const updateRateFlag: boolean = Boolean(this.massUpdateForm.get('updateRate')!.value);
    const updateParkerPayTypeFlag: boolean = Boolean(this.massUpdateForm.get('updateParkerPayType')!.value);
    const updateInvoiceDeliveryDateFlag: boolean = Boolean(this.massUpdateForm.get('updateInvoiceDeliveryDate')!.value);
    const updateTermDateDateFlag: boolean = Boolean(this.massUpdateForm.get('updateTermDate')!.value);

    if (updateTermDateDateFlag) {
      this.massUpdateForm.get('termDate')!.enable();
      termDateReadyUpdate =
        updateTermDateDateFlag &&
        this.massUpdateForm.get('termDate')!.value !== '' &&
        this.parkerStateService.parkerUpdateList.length > 0;
    } else {
      termDateReadyUpdate = false;
      this.massUpdateForm.patchValue({
        termDate: '',
      });
      this.massUpdateForm.get('termDate')!.disable();
    }

    if (updateCompanyFlag) {
      this.massUpdateForm.get('companyId')!.enable();
      let companyId = this.massUpdateForm.get('companyId')!.value;
      companyReadyUpdate =
        updateCompanyFlag &&
        this.massUpdateForm.get('companyId')!.value !== 'NONE' &&
        this.parkerStateService.parkerUpdateList.length > 0;
      if (companyId == 'NONE') this.getMarketRateListByLotNumber(this.parkerStateService.filteredParkers[0].lotNumber);
      else this.getCompanyAndMarketRateListByLotNumber(this.parkerStateService.filteredParkers[0].lotNumber, companyId);
    } else {
      companyReadyUpdate = false;
      this.massUpdateForm.patchValue({
        companyId: 'NONE',
      });
      this.massUpdateForm.get('companyId')!.disable();
    }

    if (updateRateFlag) {
      this.massUpdateForm.get('rateNumber')!.enable();
      rateReadyUpdate =
        updateRateFlag &&
        this.massUpdateForm.get('rateNumber')!.value !== 'NONE' &&
        this.parkerStateService.parkerUpdateList.length > 0;
    } else {
      rateReadyUpdate = false;
      this.massUpdateForm.patchValue({
        rateNumber: 'NONE',
      });
      this.massUpdateForm.get('rateNumber')!.disable();
    }

    if (updateParkerPayTypeFlag) {
      this.massUpdateForm.get('parkerPayType')!.enable();
      parkerPayTypeReadyUpdate =
        updateParkerPayTypeFlag &&
        this.massUpdateForm.get('parkerPayType')!.value !== 'ALL' &&
        this.parkerStateService.parkerUpdateList.length > 0;
    } else {
      parkerPayTypeReadyUpdate = false;
      this.massUpdateForm.patchValue({
        parkerPayType: 'ALL',
      });
      this.massUpdateForm.get('parkerPayType')!.disable();
    }

    if (updateInvoiceDeliveryDateFlag) {
      this.massUpdateForm.get('invoiceDeliveryMethod')!.enable();
      invoiceDeliveryMethodReadyUpdate =
        updateInvoiceDeliveryDateFlag &&
        this.massUpdateForm.get('invoiceDeliveryMethod')!.value !== 'ALL' &&
        this.parkerStateService.parkerUpdateList.length > 0;
    } else {
      invoiceDeliveryMethodReadyUpdate = false;
      this.massUpdateForm.patchValue({
        invoiceDeliveryMethod: 'ALL',
      });
      this.massUpdateForm.get('invoiceDeliveryMethod')!.disable();
    }

    this.updateButtonDisabled =
      !companyReadyUpdate &&
      !rateReadyUpdate &&
      !parkerPayTypeReadyUpdate &&
      !invoiceDeliveryMethodReadyUpdate &&
      !termDateReadyUpdate;
  }

  // getCompanyAndMarketRateListByLotNumber(lotNumber: string){
  //   this.dataService.getMarketCompanyRatesForLotNumber(lotNumber).subscribe(
  //     res => {
  //       this.marketRateList = res
  //     },
  //     (err:any) => {
  //     },
  //     () => {
  //       this.parkerStateService.marketRateList = this.marketRateList;
  //     }
  //   )
  // }

  getCompanyAndMarketRateListByLotNumber(lotNumber: string, companyId: string) {
    this.dataService.getMarketCompanyRatesForLotAndCompany(lotNumber, companyId).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {},
      () => {},
    );
  }

  getMarketRateListByLotNumber(lotNumber: string) {
    let tempMarketRateList: ILotRateList[] = [];

    this.dataService.GetUniqueMarketRatesByLotNumber(lotNumber).subscribe(
      (res) => {
        tempMarketRateList = res;
      },
      (err: any) => {},
      () => {
        //Note: Ken Moss - Hack below
        //The reason to transition data from one datastructure to another is that the base data providers output different data structures and this coalesces them into a standard structure for the UI
        let ii: number;

        this.marketRateList = [];

        for (ii = 0; ii < tempMarketRateList.length; ii++) {
          let rateList: ILotRateList = {} as ILotRateList;

          rateList.rateType = tempMarketRateList[ii].rateType;
          rateList.activeRate = tempMarketRateList[ii].amount;
          rateList.description = tempMarketRateList[ii].description;
          rateList.rateNumber = tempMarketRateList[ii].rateNumber;

          this.marketRateList.push(rateList);
        }
      },
    );
  }

  getStallDescriptionListForLotNumber(lotNumber: string) {
    this.dataService.getLotStallDescriptionList(lotNumber).subscribe(
      (res) => {
        this.parkerStateService.stallDescriptions = res;
      },
      (err: any) => {},
      () => {},
    );
  }
}
